/**
 * Speed/Unit Display Normalization Utilities
 * Monarch data: Speed in Mbps, unit field determines display format
 */

/**
 * Normalize Monarch speed/unit for display
 * "Mbps" → as-is, "Gig" → divide by 1000
 */
export function normalizeMonarchSpeedForDisplay(
  speed: string | number,
  unit: string
): { speed: string; unit: string } {
  // Input validation
  const speedNum = typeof speed === "string" ? parseFloat(speed) : speed;

  if (isNaN(speedNum)) {
    console.warn(
      `[normalize-speed-display] Invalid speed value: "${speed}". Defaulting to "0".`
    );
    return { speed: "0", unit: "Mbps" };
  }

  if (speedNum < 0) {
    console.warn(
      `[normalize-speed-display] Negative speed value: "${speed}". Using absolute value.`
    );
    return normalizeMonarchSpeedForDisplay(Math.abs(speedNum), unit);
  }

  const normalizedUnit = unit.toLowerCase();

  if (normalizedUnit === "gig") {
    const gigSpeed = speedNum / 1000;
    return { speed: gigSpeed.toString(), unit: "Gig" };
  }

  if (normalizedUnit === "mbps") {
    return { speed: speedNum.toString(), unit: "Mbps" };
  }

  console.warn(
    `[normalize-speed-display] Unexpected unit: "${unit}". Defaulting to "Mbps".`
  );
  return { speed: speedNum.toString(), unit: "Mbps" };
}

/**
 * Convert API speed to match Monarch unit preference
 * Monarch unit takes priority when merging
 */
export function convertApiSpeedToMonarchUnit(
  apiSpeed: string,
  apiUnit: string,
  monarchUnit: string
): { speed: string; unit: string } {
  // Input validation
  const apiSpeedNum = parseFloat(apiSpeed);

  if (isNaN(apiSpeedNum)) {
    console.warn(
      `[normalize-speed-display] Invalid API speed value: "${apiSpeed}". Defaulting to "0".`
    );
    return { speed: "0", unit: monarchUnit };
  }

  if (apiSpeedNum < 0) {
    console.warn(
      `[normalize-speed-display] Negative API speed value: "${apiSpeed}". Using absolute value.`
    );
    return convertApiSpeedToMonarchUnit(
      Math.abs(apiSpeedNum).toString(),
      apiUnit,
      monarchUnit
    );
  }

  const normalizedApiUnit = apiUnit.toLowerCase();
  const normalizedMonarchUnit = monarchUnit.toLowerCase();

  if (normalizedApiUnit === normalizedMonarchUnit) {
    const standardUnit = normalizedMonarchUnit === "gig" ? "Gig" : "Mbps";
    return { speed: apiSpeed, unit: standardUnit };
  }

  // Convert between units
  if (normalizedMonarchUnit === "mbps" && normalizedApiUnit === "gig") {
    const mbpsSpeed = apiSpeedNum * 1000;
    return { speed: mbpsSpeed.toString(), unit: "Mbps" };
  }

  if (normalizedMonarchUnit === "gig" && normalizedApiUnit === "mbps") {
    const gigSpeed = apiSpeedNum / 1000;
    return { speed: gigSpeed.toString(), unit: "Gig" };
  }

  console.warn(
    `[normalize-speed-display] Unexpected unit conversion: API="${apiUnit}", Monarch="${monarchUnit}".`
  );
  const standardUnit = normalizedApiUnit === "gig" ? "Gig" : "Mbps";
  return { speed: apiSpeed, unit: standardUnit };
}

/**
 * Normalize speed/unit to Mbps for comparison
 * Used to match Monarch configs with API offers
 */
export function normalizeToMbps(speed: string | number, unit: string): number {
  const speedNum = typeof speed === "string" ? parseFloat(speed) : speed;

  if (isNaN(speedNum)) {
    console.warn(
      `[normalize-speed-display] Invalid speed for comparison: "${speed}".`
    );
    return 0;
  }

  const normalizedUnit = unit.toLowerCase();

  if (normalizedUnit === "gig") {
    return speedNum * 1000;
  }

  if (normalizedUnit === "mbps") {
    return speedNum;
  }

  console.warn(
    `[normalize-speed-display] Unknown unit: "${unit}". Assuming Mbps.`
  );
  return speedNum;
}

/**
 * Check if two speed/unit pairs match
 */
export function speedsMatch(
  speed1: string | number,
  unit1: string,
  speed2: string | number,
  unit2: string
): boolean {
  const normalizedSpeed1 = normalizeToMbps(speed1, unit1);
  const normalizedSpeed2 = normalizeToMbps(speed2, unit2);

  return normalizedSpeed1 === normalizedSpeed2;
}
